import { z } from 'zod';
import { agentChatModelSchema } from '../enums/agent.js';
import { StatusTypeSchema } from '../enums/status.js';

export const promptMessageSchema = z.object({
  role: z.enum(['system', 'user']),
  content: z.string(),
});
export type PromptMessage = z.infer<typeof promptMessageSchema>;

export const promptKindSchema = z.enum([
  'default',
  'agent-system-prompt',
  'compliance-check',
  'chat-name-generation',
  'suggested-followups-generation',
  'query-entity-detection',
]);
export type PromptKind = z.infer<typeof promptKindSchema>;

export const complianceCheckStructuredOutputSchema: Record<string, unknown> = {
  type: 'object',
  properties: {
    isQuestionCompliant: {
      type: 'boolean',
    },
    nonComplianceReason: {
      type: 'string',
      format: 'markdown',
    },
  },
  required: ['isQuestionCompliant', 'nonComplianceReason'],
  additionalProperties: false,
};

export const complianceCheckResultSchema = z.object({
  isQuestionCompliant: z.boolean(),
  nonComplianceReason: z.string(),
});
export type ComplianceCheckResult = z.infer<typeof complianceCheckResultSchema>;

export const chatNameGenerationStructuredOutputSchema: Record<string, unknown> = {
  type: 'object',
  properties: {
    value: {
      type: 'string',
    },
  },
  required: ['value'],
  additionalProperties: false,
};

export const chatNameGenerationResultSchema = z.object({
  value: z.string(),
});
export type ChatNameGenerationResult = z.infer<typeof chatNameGenerationResultSchema>;

export const suggestedFollowupsGenerationStructuredOutputSchema: Record<string, unknown> = {
  type: 'object',
  properties: {
    followupQuestions: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          value: {
            type: 'string',
          },
        },
        required: ['value'],
        additionalProperties: false,
      },
    },
  },
  required: ['followupQuestions'],
  additionalProperties: false,
};

export const suggestedFollowupsGenerationResultSchema = z.object({
  followupQuestions: z.array(z.object({ value: z.string() })),
});
export type SuggestedFollowupsGenerationResult = z.infer<typeof suggestedFollowupsGenerationResultSchema>;

export const queryEntityDetectionStructuredOutputSchema: Record<string, unknown> = {
  type: 'object',
  properties: {
    entities: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: {
            anyOf: [{ type: 'string' }, { type: 'integer' }, { type: 'null' }],
          },
          name: {
            type: 'string',
          },
          kind: {
            type: 'string',
          },
        },
        required: ['id', 'name', 'kind'],
        additionalProperties: false,
      },
    },
    rewrittenQuery: {
      type: ['string', 'null'],
    },
  },
  required: ['entities', 'rewrittenQuery'],
  additionalProperties: false,
};

export const queryEntityDetectionEntitySchema = z.object({
  id: z.union([z.string(), z.number().int()]).nullable(),
  name: z.string(),
  kind: z.string(),
});

export const queryEntityDetectionResultSchema = z.object({
  entities: z.array(queryEntityDetectionEntitySchema),
  rewrittenQuery: z.string().nullable(),
});
export type QueryEntityDetectionResult = z.infer<typeof queryEntityDetectionResultSchema>;

const fixedStructuredOutputSchemas: Record<
  Extract<
    PromptKind,
    'compliance-check' | 'chat-name-generation' | 'suggested-followups-generation' | 'query-entity-detection'
  >,
  Record<string, unknown>
> = {
  'compliance-check': complianceCheckStructuredOutputSchema,
  'chat-name-generation': chatNameGenerationStructuredOutputSchema,
  'suggested-followups-generation': suggestedFollowupsGenerationStructuredOutputSchema,
  'query-entity-detection': queryEntityDetectionStructuredOutputSchema,
};

export function getFixedStructuredOutputSchemaForPromptKind(
  kind: PromptKind
): Record<string, unknown> | null {
  if (
    kind === 'compliance-check' ||
    kind === 'chat-name-generation' ||
    kind === 'suggested-followups-generation' ||
    kind === 'query-entity-detection'
  ) {
    return structuredClone(fixedStructuredOutputSchemas[kind]);
  }

  return null;
}

export const promptListItemSchema = z.object({
  id: z.number(),
  identifier: z.uuid(),
  name: z.string(),
  description: z.string(),
  kind: promptKindSchema,
  status: StatusTypeSchema,
  version: z.number(),
  createdOn: z.coerce.date(),
  queriesSubmitted: z.number(),
});
export type PromptListItem = z.infer<typeof promptListItemSchema>;

export const promptDetailsSchema = z.object({
  id: z.number(),
  identifier: z.uuid(),
  name: z.string(),
  description: z.string(),
  kind: promptKindSchema,
  status: StatusTypeSchema,
  version: z.number(),
  messages: z.array(promptMessageSchema),
  inputSchema: z.record(z.string(), z.unknown()),
  lastNMessagePairsToInclude: z.number().int().nonnegative().nullable().optional(),
  isActive: z.boolean(),
  llmModel: agentChatModelSchema,
  structuredOutputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
  createdOn: z.coerce.date(),
});
export type PromptDetails = z.infer<typeof promptDetailsSchema>;
